import differenceWith from 'lodash/differenceWith';
import isEqual from 'lodash/isEqual';
import last from 'lodash/last';

const seasonRegExp = /^(FW|SS)([0-9]{2})/;

class SeasonData {
  static fallWinterName() {
    return 'FW';
  }
  static springSummerName() {
    return 'SS';
  }
  static classicSeasonName() {
    return 'Classic';
  }

  static seasonNames() {
    return [this.fallWinterName(), this.springSummerName()];
  }

  static fromSeasonString(season) {
    if (season === SeasonData.classicSeasonName()) {
      return new SeasonData(season, null);
    }

    const matches = season.match(seasonRegExp);
    const name = matches[1];
    const year = parseInt(matches[2]);
    return new SeasonData(name, year);
  }

  constructor(name, year) {
    this.name = name;
    this.year = year;
  }

  isClassicSeason() {
    return this.name === SeasonData.classicSeasonName();
  }

  isOlderThan(season) {
    if (this.isClassicSeason()) {
      return true;
    }
    if (season.isClassicSeason()) {
      return false;
    }

    if (this.year < season.year) {
      return true;
    }

    return (
      this.year === season.year &&
      this.name === SeasonData.springSummerName() &&
      season.name !== SeasonData.springSummerName()
    );
  }

  firstCalendarSeason() {
    if (this.isClassicSeason()) {
      return this.name;
    }

    return this.name[0];
  }

  secondCalendarSeason() {
    if (this.isClassicSeason()) {
      return this.name;
    }

    return this.name[1];
  }

  previousSeason() {
    if (this.isClassicSeason()) {
      return this;
    }

    const previousSeasonYear = this.name === SeasonData.springSummerName() ? this.year - 1 : this.year;
    const seasonIndex = SeasonData.seasonNames().indexOf(this.name);
    const previousSeasonIndex = (seasonIndex + SeasonData.seasonNames().length - 1) % SeasonData.seasonNames().length;
    const previousSeasonName = SeasonData.seasonNames()[previousSeasonIndex];

    return new SeasonData(previousSeasonName, previousSeasonYear);
  }

  displayName() {
    if (this.isClassicSeason()) {
      return '';
    }

    const startName = this.firstCalendarSeason();
    const endName = this.secondCalendarSeason();
    const nextYearString = (this.year + 1).toString();
    const currentYearString = this.year.toString();
    if (this.name === SeasonData.fallWinterName()) {
      return `${startName}${currentYearString} - ${endName}${nextYearString}`;
    } else {
      return `${startName}${currentYearString} - ${endName}${currentYearString}`;
    }
  }

  toString() {
    if (this.isClassicSeason()) {
      return this.name;
    } else {
      return this.name + this.year.toString();
    }
  }
}

const buildLast4Seasons = currentSeason => {
  let season = currentSeason;
  const seasons = [season];

  for (let i = 0; i < 3; i++) {
    season = season.previousSeason();
    seasons.push(season);
  }

  return seasons;
};

const latestKnownSeason = (currentSeason, availableSeasons, selectedSeasons) => {
  const seasonSorter = (a, b) => (a.isOlderThan(b) ? -1 : 1);
  const allSeasons = [currentSeason].concat(availableSeasons, selectedSeasons);
  const sortedSeasons = allSeasons.sort(seasonSorter);
  return last(sortedSeasons);
};

const generateFiltersModel = (currentSeason, availableSeasons, selectedSeasons) => {
  const latestSeason = latestKnownSeason(currentSeason, availableSeasons, selectedSeasons);

  const last4Seasons = buildLast4Seasons(latestSeason);

  let filtersUnderPreviousSection = differenceWith(availableSeasons, last4Seasons, isEqual);

  // extend previous filters section with selected filters that are not the last 4 seasons
  let selectedOldSeasons = differenceWith(selectedSeasons, last4Seasons, isEqual);
  selectedOldSeasons = differenceWith(selectedOldSeasons, filtersUnderPreviousSection, isEqual);
  filtersUnderPreviousSection = filtersUnderPreviousSection.concat(selectedOldSeasons);

  const finalFilterList = last4Seasons.map(seasonData => ({ data: [seasonData], text: seasonData.displayName() }));
  finalFilterList.push({ data: filtersUnderPreviousSection, text: 'PREVIOUS SEASONS' });

  return finalFilterList;
};

const getSeasonsDisplayableForm = (seasons, currentSeason, availableSeasons) => {
  const calendarAvailableSeason = availableSeasons.map(el => SeasonData.fromSeasonString(el));
  const latestSeason = latestKnownSeason(SeasonData.fromSeasonString(currentSeason), calendarAvailableSeason, []);
  const calendarSeasons = buildLast4Seasons(latestSeason);
  const selectedCalendarSeasons = [];
  let selectedPrevSeason = false;
  seasons.forEach(season => {
    const seasonData = SeasonData.fromSeasonString(season);
    if (!seasonData.isOlderThan(calendarSeasons[3])) {
      selectedCalendarSeasons.push(seasonData.displayName());
    } else {
      selectedPrevSeason = true;
    }
  });
  if (selectedPrevSeason) {
    selectedCalendarSeasons.push('Previous Seasons');
  }
  return selectedCalendarSeasons;
};

export { buildLast4Seasons, generateFiltersModel, getSeasonsDisplayableForm, latestKnownSeason, SeasonData };
